/**
 * RoomTemplatesEditor — admin tool to view and edit Room Templates (Airtable
 * tblslHjXgMJb59po0), the small global catalog every Room in the Concepts/Rooms hierarchy and
 * the Program Model picks from. Moves the day-to-day upkeep of this catalog (mainly tuning
 * Structural Base Factor — see docs/PROGRAM_MODEL.md "Cost Factor — derived structural cost
 * model") off direct Airtable editing, mirroring WbsTemplateEditor.jsx's pattern.
 *
 * Opened via `open-room-templates-editor`; mounted once in OutlineView; open to anyone who can edit templates (admin or checklist-templates), 2026-09-24.
 * Grouped by Classification (the $/SF category on the Control Budget Model); each row edits
 * Name, Project Type, Structural Base Factor, ID Room Book, and — on a second line — a free-text
 * rationale for the factor (`Structural Base Factor Notes`, e.g. "heavy casework, countertops,
 * plumbing"), so the reasoning behind a number stays attached to it, not just in a doc/commit
 * message. Edits persist on blur directly to Airtable via services/design.js (no separate
 * Supabase-backed API, unlike WBS).
 */

import { useEffect, useRef, useState } from 'react'
import { hasModule, isSeatLoaded } from '../../services/tenancy.js'
import { getRoomTemplatesFull, updateRoomTemplate, createRoomTemplate, deleteRoomTemplate } from '../../services/design.js'
import { alertDialog, confirmDialog } from '../Dialog.jsx'

// Mirrors the Classification singleSelect's own choice order in Airtable.
const CLASSIFICATIONS = ['Occupiable', 'Garage', 'Storage Utility', 'Elevated Deck', 'Covered Patio', 'Site Improvement', 'Pool Spa']
const PROJECT_TYPES = ['Residential', 'Commercial']

function pctToStr(frac) { return frac == null ? '' : String(Math.round(Number(frac) * 100)) }
function strToPct(str) { return str.trim() === '' ? null : Number(str) / 100 }

export default function RoomTemplatesEditor() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  // New rows sort alphabetically into their (often long) classification group with no other
  // visual cue — clicking + Add otherwise looks like nothing happened. Track the row's id so
  // we can scroll it into view and focus its Name input once it renders (Round 8).
  const [newRowId, setNewRowId] = useState(null)
  const nameInputRefs = useRef({})

  useEffect(() => {
    function onOpen() { setOpen(true); load() }
    window.addEventListener('open-room-templates-editor', onOpen)
    return () => window.removeEventListener('open-room-templates-editor', onOpen)
  }, [])

  useEffect(() => {
    if (!newRowId) return
    const input = nameInputRefs.current[newRowId]
    if (input) {
      input.scrollIntoView({ block: 'center', behavior: 'smooth' })
      input.focus()
      input.select()
    }
    setNewRowId(null)
  }, [newRowId, rows])

  async function load() {
    setLoading(true); setError(null)
    try { setRows(await getRoomTemplatesFull()) }
    catch (e) { setError(e.message || 'Failed to load') }
    finally { setLoading(false) }
  }

  function close() { setOpen(false); setRows([]); setError(null) }

  function setLocal(id, field, value) {
    setRows(prev => prev.map(r => r.id === id ? { ...r, fields: { ...r.fields, [field]: value } } : r))
  }

  async function persist(id, fields) {
    setBusyId(id)
    try { await updateRoomTemplate(id, fields) }
    catch (e) { await alertDialog({ title: 'Save failed', message: e.message }); load() }
    finally { setBusyId(null) }
  }

  function toggleProjectType(row, type) {
    const cur = row.fields['Project Type'] || []
    const next = cur.includes(type) ? cur.filter(t => t !== type) : [...cur, type]
    setLocal(row.id, 'Project Type', next)
    persist(row.id, { 'Project Type': next })
  }

  async function addRow(cls) {
    setBusyId('new')
    try {
      const rec = await createRoomTemplate({ Name: 'New template', Classification: cls })
      setRows(prev => [...prev, rec])
      setNewRowId(rec.id)
    } catch (e) { await alertDialog({ title: 'Add failed', message: e.message }) }
    finally { setBusyId(null) }
  }

  async function removeRow(row) {
    const usedBy = (row.fields['Rooms and Spaces'] || []).length
    const label = row.fields['Name'] || 'this template'
    const message = usedBy > 0
      ? 'Delete “' + label + '”? ' + usedBy + ' existing room(s) currently use this template — they will lose their Room Template link (their Cost Factor will fall back to no baseline).'
      : 'Delete “' + label + '”? No rooms currently use this template.'
    if (!(await confirmDialog({ title: 'Delete room template?', message, confirmLabel: 'Delete template', danger: true }))) return
    setBusyId(row.id)
    try {
      await deleteRoomTemplate(row.id)
      setRows(prev => prev.filter(r => r.id !== row.id))
    } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message }) }
    finally { setBusyId(null) }
  }

  if (!open) return null
  if (isSeatLoaded() && !hasModule('admin') && !hasModule('checklist-templates')) {   // anyone who can edit templates (Matt, 2026-09-24)
    return (
      <>
        <div style={S.backdrop} onClick={close} />
        <div style={S.modal}><div style={S.restricted}>Only people who can edit templates can open this.</div><div style={S.btns}><button style={S.ghost} onClick={close}>Close</button></div></div>
      </>
    )
  }

  return (
    <>
      <div style={S.backdrop} onClick={close} />
      <div style={S.modal} role="dialog" aria-label="Room Templates">
        <div style={S.headRow}>
          <span style={S.kicker}>Room Templates</span>
          <button style={S.x} onClick={close} aria-label="Close">×</button>
        </div>
        <div style={S.sub}>
          The room-type catalog every Room picks from. Structural Base Factor is the room type's
          baseline structural cost adjustment (built-ins, plumbing, fixture density — not finish
          quality) feeding each room's derived Cost Factor. Edits save immediately.
        </div>
        {error && <div style={S.err}>{error}</div>}

        <div style={S.body}>
          {loading ? <div style={S.muted}>Loading…</div> : CLASSIFICATIONS.map(cls => {
            const inCls = rows.filter(r => (r.fields['Classification'] || '') === cls)
              .sort((a, b) => (a.fields['Name'] || '').localeCompare(b.fields['Name'] || ''))
            if (!inCls.length) return null
            return (
              <div key={cls} style={S.group}>
                <div style={S.groupHead}>
                  <span style={S.groupName}>{cls}</span>
                  <span style={S.groupCount}>{inCls.length}</span>
                  <button style={S.addBtn} disabled={busyId === 'new'} onClick={() => addRow(cls)}>+ Add</button>
                </div>
                {cls === 'Pool Spa' && (
                  <div style={S.groupNote}>Pool/Spa rooms are priced as a fixed lump sum by size template, not area × Cost Factor — Structural Base Factor has no effect here.</div>
                )}
                <div style={S.table}>
                  <div style={{ ...S.tr, ...S.thead }}>
                    <span style={S.cName}>Name</span>
                    <span style={S.cType}>Project Type</span>
                    <span style={S.cFactor}>Structural Base Factor</span>
                    <span style={S.cBook}>ID Room Book</span>
                    <span style={S.cDel}></span>
                  </div>
                  {inCls.map(row => (
                    <div key={row.id} style={{ opacity: busyId === row.id ? 0.5 : 1, paddingBottom: 3, borderBottom: '0.5px solid var(--aj-border)' }}>
                      <div style={S.tr}>
                        <input ref={el => { nameInputRefs.current[row.id] = el }}
                          style={{ ...S.inp, ...S.cName }} value={row.fields['Name'] || ''}
                          onChange={e => setLocal(row.id, 'Name', e.target.value)}
                          onBlur={e => persist(row.id, { Name: e.target.value.trim() })} />
                        <span style={S.cType}>
                          {PROJECT_TYPES.map(t => (
                            <label key={t} style={S.chk}>
                              <input type="checkbox" checked={(row.fields['Project Type'] || []).includes(t)}
                                onChange={() => toggleProjectType(row, t)} />
                              {t === 'Residential' ? 'Res' : 'Com'}
                            </label>
                          ))}
                        </span>
                        <span style={{ ...S.cFactor, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <input type="number" style={{ ...S.inp, width: 56, textAlign: 'right' }}
                            value={pctToStr(row.fields['Structural Base Factor'])}
                            onChange={e => setLocal(row.id, 'Structural Base Factor', e.target.value === '' ? null : Number(e.target.value) / 100)}
                            onBlur={e => persist(row.id, { 'Structural Base Factor': strToPct(e.target.value) })} />
                          <span style={{ color: 'var(--aj-text-mid)', fontSize: 11 }}>%</span>
                        </span>
                        <span style={{ ...S.cBook, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!row.fields['ID Room Book']}
                            onChange={e => { setLocal(row.id, 'ID Room Book', e.target.checked); persist(row.id, { 'ID Room Book': e.target.checked }) }} />
                        </span>
                        <button style={{ ...S.cDel, ...S.delBtn }} title="Delete" onClick={() => removeRow(row)}>×</button>
                      </div>
                      <input style={S.notesInp} placeholder="Why this % — e.g. “heavy casework, countertops, plumbing”"
                        value={row.fields['Structural Base Factor Notes'] || ''}
                        onChange={e => setLocal(row.id, 'Structural Base Factor Notes', e.target.value)}
                        onBlur={e => persist(row.id, { 'Structural Base Factor Notes': e.target.value.trim() })} />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div style={S.btns}><button style={S.ghost} onClick={close}>Done</button></div>
      </div>
    </>
  )
}

const MONO = '"DM Mono",monospace'
const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1400 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(880px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 14, zIndex: 1401, padding: '16px 18px 14px', boxShadow: '0 18px 60px rgba(0,0,0,0.28)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)' },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  kicker: { fontFamily: 'Montserrat,sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  x: { fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  sub: { fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.5 },
  err: { marginTop: 8, fontSize: 12.5, color: '#c0392b' },
  body: { marginTop: 12, overflowY: 'auto', flex: 1 },
  muted: { fontSize: 12.5, color: 'var(--aj-text-muted)', padding: 12 },
  group: { marginBottom: 14 },
  groupHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '0.5px solid var(--aj-border)' },
  groupName: { fontFamily: 'Montserrat,sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  groupCount: { fontFamily: MONO, fontSize: 10.5, color: 'var(--aj-text-mid)', flex: 1 },
  groupNote: { fontSize: 11, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 0 2px', lineHeight: 1.4 },
  addBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#1a6fa8', background: 'none', border: '0.5px solid rgba(26,111,168,0.4)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' },
  table: { marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 },
  tr: { display: 'grid', gridTemplateColumns: '1.6fr 100px 150px 90px 26px', gap: 6, alignItems: 'center' },
  thead: { fontSize: 9.5, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Montserrat,sans-serif', paddingBottom: 2 },
  inp: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', width: '100%', boxSizing: 'border-box' },
  notesInp: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontStyle: 'italic', padding: '3px 6px', marginTop: 3, borderRadius: 4, border: '0.5px dashed var(--aj-border)', color: 'var(--aj-text-muted)', background: 'transparent', width: '100%', boxSizing: 'border-box' },
  cName: {}, cType: { display: 'flex', gap: 8 }, cFactor: {}, cBook: {}, cDel: { textAlign: 'center' },
  delBtn: { fontSize: 16, color: '#b8540a', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 },
  chk: { display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--aj-text-mid)', cursor: 'pointer' },
  btns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)' },
  ghost: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
  restricted: { fontSize: 13, color: 'var(--aj-text-muted)', padding: 20, textAlign: 'center' },
}
